<template>
  <OTable
    v-show="!initialLoading"
    ref="tableRef"
    v-bind="attrs"
    v-model:pagination="pagination"
    :rows="rows"
    :columns="columns"
    :loading="loading"
    :filter="searchFilter"
    row-key="id"
    binary-state-sort
    class="table-base"
    primary
    @request="onRequest"
  >
    <template v-if="header" #top>
      <OTableHeaderBase
        :filter="searchFilter"
        :show-rows-per-page="showRowsPerPage"
        :rows-per-page="pagination.rowsPerPage"
        @update="updateModels"
      />
    </template>

    <template v-for="slot in Object.keys(slots)" #[slot]="slotProps">
      <slot :name="slot" v-bind="slotProps" />
    </template>

    <template #bottom="slotProps">
      <OTableFooterBase
        :downloadable="showDownloadButton"
        :print-button="showPrintButton"
        :rows="rows"
        :columns="columns"
        :slot-props="slotProps"
        :position="positionFooter"
        :pagination="pagination"
        @update="(val) => updatePagination(val)"
      />
    </template>
  </OTable>

  <BaseLoader
    v-show="initialLoading"
    class="min-h-[300px] grid place-items-center"
  />
  <!-- <SkeletonTable v-if="loading" class="mt-20 px-16" /> -->
</template>

<script setup>
import {
  computed,
  onMounted,
  provide,
  ref,
  useAttrs,
  useSlots,
  watch,
} from 'vue'

import { api } from 'js/modules/axios'
import { NotifyError } from 'js/vue/boot/Notify'
import { useI18n } from 'vue-i18n'
import OTable from './OTable.vue'
import OTableFooterBase from './OTableFooterBase.vue'
import OTableHeaderBase from './OTableHeaderBase.vue'
// import SkeletonTable from 'components/Skeleton/SkeletonTable.vue';
import { errorDefault } from 'js/utils/error'
import BaseLoader from 'components/Loader/BaseLoader.vue'
const { URLS } = api.defaults
const { t } = useI18n()

const attrs = useAttrs()
const slots = useSlots()

const props = defineProps({
  columns: { type: Array, default: () => [] },
  showRowsPerPage: { type: Boolean, default: true },
  header: { type: Boolean, default: true },
  showDownloadButton: { type: Boolean, default: false },
  showPrintButton: { type: Boolean, default: false },
  positionFooter: { type: String, default: 'right' },
  apiUrl: { type: String, required: true },
})

const rows = ref([])
const tableRef = ref()
const searchFilter = ref('')
const loading = ref(true)
const pagination = ref({
  sortBy: 'desc',
  descending: false,
  page: 1,
  rowsPerPage: 10,
  rowsNumber: 10,
})
const currentOrder = ref('')

function updateTable() {
  tableRef.value?.componentRef.requestServerInteraction()
}

function updateModels(val) {
  searchFilter.value = val.filter
  pagination.value.rowsPerPage = val.rowsPerPage

  updateTable()
}

function updatePagination(val) {
  console.log('updatePagination')
  pagination.value = val
  updateTable()
}
const initialLoading = ref(true)

async function fetchFromServer(startRow, count, search, sortBy, descending) {
  
  let data = []
  const page = startRow / count + 1

  if (sortBy !== 'desc') {
    currentOrder.value = currentOrder.value === sortBy ? `-${sortBy}` : sortBy
  }

  const url = `${props.apiUrl}
  ${search ? `&search=${search}` : ''}
  ${currentOrder.value ? `&ordering=${currentOrder.value}` : ''}
  &limit=${count}
  &page=${page}
  `

  let response
  try {
    response = await api.get(url)
    data =
      response.data.itens?.slice() ||
      response.data.results?.slice() ||
      response.data.result?.slice()
  } catch (error) {
    errorDefault(t('error.data'), error)
  } finally {
    initialLoading.value = false
  }

  return { data, rowsNumber: response.data.total || response.data.count }
}

async function onRequest(requestProps) {
  const { page, rowsPerPage, sortBy, descending } = requestProps.pagination
  const search = requestProps.filter

  loading.value = true
  rows.value = []
  const fetchCount =
    rowsPerPage === 0 ? pagination.value.rowsNumber : rowsPerPage
  const startRow = (page - 1) * rowsPerPage
  const returnedData = await fetchFromServer(
    startRow,
    fetchCount,
    search,
    sortBy,
    descending
  )
  console.log(returnedData, 'return data')

  pagination.value.rowsNumber = returnedData.rowsNumber
  rows.value.splice(0, rows.value.length, ...returnedData.data)

  pagination.value.page = page
  pagination.value.rowsPerPage = rowsPerPage
  pagination.value.sortBy = sortBy
  pagination.value.descending = descending

  loading.value = false
}

provide('pagination', pagination)
onMounted(() => {
  updateTable()
})

defineExpose({
  updateTable,
  tableRows: rows.value,
  updatePagination,
  updateModels,
  initialLoading,
})
</script>

<style lang="sass" scoped></style>
